import React, { useState, useEffect } from "react";
import { useMiniERP } from "./hooks/useMiniERP";
import { useStatusBanner, StatusBanner } from "./components/MiniERPStatusBanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faToggleOn,
  faToggleOff,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";

const categories = [
  "Merchandisable",
  "Raw Material",
  "Finished Goods",
  "Consumables",
  "Packing Material",
  "Others",
];

const MiniERPInventory = ({ authState }) => {
  const { fetchInventory, createInventory, updateInventory, toggleInventory } =
    useMiniERP();

  const [inventoryList, setInventoryList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const { status, showSuccess, showError } = useStatusBanner();
  const [listStatus, setListStatus] = useState(null);

  const [formData, setFormData] = useState({
    itemCode: "",
    name: "",
    category: "Merchandisable",
    unit: "PCS",
    currentStock: 0,
    isActive: true,
  });

  const loadInventory = async () => {
    const res = await fetchInventory();
    setInventoryList(res?.data || []);
  };

  useEffect(() => {
    loadInventory();
  }, []);

  const filteredInventory = inventoryList.filter((item) => {
    const matchesSearch =
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.itemCode?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateInventory(editingItem._id, formData);
        showSuccess("Inventory item updated successfully!");
      } else {
        await createInventory(formData);
        showSuccess("Inventory item created successfully!");
      }
      await loadInventory();
      setTimeout(() => { setShowModal(false); resetForm(); }, 700);
    } catch (err) {
      showError(err.message);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleInventory(id);
      await loadInventory();
      setListStatus({ type: 'success', message: 'Status updated.' });
      setTimeout(() => setListStatus(null), 2500);
    } catch (err) {
      setListStatus({ type: 'error', message: 'Toggle failed: ' + err.message });
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      itemCode: item.itemCode,
      name: item.name,
      category: item.category,
      unit: item.unit || "PCS",
      currentStock: item.currentStock || 0,
      isActive: item.isActive,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      itemCode: "",
      name: "",
      category: "Merchandisable",
      unit: "PCS",
      currentStock: 0,
      isActive: true,
    });
    setEditingItem(null);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-2 md:p-6 overflow-x-hidden">
      <div className="max-w-6xl mx-auto overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-lg md:text-xl lg:text-3xl font-bold text-cyan-200">
              Inventory Management
            </h1>
            <p className="text-[10px] md:text-xs lg:text-sm text-gray-400">
              Stock Items • Categories • Current Balance
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="mt-4 md:mt-0 bg-cyan-600 hover:bg-cyan-500 w-fit px-2 py-1 rounded-2xl flex items-center gap-2 text-sm lg:text-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> New Item
          </button>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 rounded-2xl p-3 mb-2 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-4 top-3.5 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search by item code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full text-xs lg:text-sm bg-gray-700 border border-gray-600 pl-11 py-1 rounded-xl text-gray-200"
            />
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-gray-700 border border-gray-600 text-xs lg:text-sm px-2 py-1 rounded-xl text-gray-200 min-w-[180px]"
          >
            <option value="All">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Inventory Table */}
        {listStatus && <div className="mb-4"><StatusBanner status={listStatus} /></div>}
        <div className="bg-gray-800 rounded-3xl overflow-hidden">
          {/* ================= MOBILE CARDS ================= */}
          <div className="block lg:hidden space-y-3">
            {filteredInventory.map((item) => (
              <div
                key={item._id}
                className="bg-gray-800 border border-gray-700 rounded-2xl p-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-cyan-300 font-mono text-xs">
                      {item.itemCode}
                    </div>
                    <div className="text-base font-semibold text-white">
                      {item.name}
                    </div>
                  </div>

                  <button
                    onClick={() => openEditModal(item)}
                    className="text-cyan-400 text-lg w-fit px-1 py-1"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="px-2 py-1 rounded-full bg-cyan-900/40 text-cyan-300 text-[11px]">
                    {item.category}
                  </span>

                  <span className="px-2 py-1 rounded-full bg-gray-700 text-gray-300 text-[11px]">
                    {item.unit || "PCS"}
                  </span>

                  <span className="px-2 py-1 rounded-full bg-emerald-900/40 text-emerald-300 text-[11px]">
                    Stock : {item.currentStock || 0}
                  </span>
                </div>

                <div className="mt-4 flex justify-between items-center">
                  <span className="text-xs text-gray-400">Status</span>

                  <button
                    onClick={() => handleToggle(item._id)}
                    className="text-2xl text-cyan-400 w-fit px-1 py-1"
                  >
                    <FontAwesomeIcon
                      icon={item.isActive ? faToggleOn : faToggleOff}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* ================= DESKTOP TABLE ================= */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-2 px-3 text-gray-400 font-medium">
                    Item Code
                  </th>
                  <th className="text-left py-2 px-3 text-gray-400 font-medium">
                    Item Name
                  </th>
                  <th className="text-left py-2 px-3 text-gray-400 font-medium">
                    Category
                  </th>
                  <th className="text-center py-2 px-3 text-gray-400 font-medium">
                    Unit
                  </th>
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">
                    Current Stock
                  </th>
                  <th className="text-center py-2 px-3 text-gray-400 font-medium">
                    Status
                  </th>
                  <th className="text-center py-2 px-3 text-gray-400 font-medium">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-700">
                {filteredInventory.map((item) => (
                  <tr
                    key={item._id}
                    className="hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="py-2 px-3 font-mono text-cyan-300">
                      {item.itemCode}
                    </td>
                    <td className="py-2 px-3 font-medium">{item.name}</td>

                    <td className="py-2 px-3">
                      <span className="px-3 py-1 bg-gray-700 rounded-full text-sm text-gray-300">
                        {item.category}
                      </span>
                    </td>

                    <td className="py-2 px-3 text-center">
                      {item.unit || "PCS"}
                    </td>

                    <td className="py-2 px-3 text-right font-semibold text-emerald-400">
                      {item.currentStock || 0}
                    </td>

                    <td className="py-2 px-3 text-center">
                      <button
                        onClick={() => handleToggle(item._id)}
                        className="text-2xl w-fit px-1 py-1 text-gray-400 hover:text-cyan-400"
                      >
                        <FontAwesomeIcon
                          icon={item.isActive ? faToggleOn : faToggleOff}
                        />
                      </button>
                    </td>

                    <td className="py-2 px-3 text-center">
                      <button
                        onClick={() => openEditModal(item)}
                        className="text-xl w-fit px-1 py-1 text-gray-400 hover:text-cyan-400"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredInventory.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              No inventory items found. Create your first item.
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3">
          <div className="bg-gray-800 rounded-3xl w-full max-w-lg p-8">
            <h2 className="text-2xl font-bold text-cyan-200 mb-6">
              {editingItem ? "Edit Item" : "New Inventory Item"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-gray-400 mb-1">Item Code</label>
                <input
                  type="text"
                  value={formData.itemCode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      itemCode: e.target.value.toUpperCase(),
                    })
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 font-mono"
                  disabled={!!editingItem}
                  required
                />
                {editingItem && (
                  <p className="text-xs text-gray-500 mt-1">
                    Item code cannot be changed after creation.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-gray-400 mb-1">Item Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-2 py-1"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-400 mb-1">Unit</label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        unit: e.target.value.toUpperCase(),
                      })
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 mb-1">
                  {editingItem ? "Current Stock" : "Opening Stock"}
                </label>
                <input
                  type="number"
                  value={formData.currentStock}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      currentStock: Number(e.target.value),
                    })
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                />
                {editingItem && (
                  <p className="text-xs text-gray-500 mt-1">
                    Prefer recording a Stock In/Out transaction instead of
                    editing this directly, so the stock trail stays accurate.
                  </p>
                )}
              </div>

              <StatusBanner status={status} />

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 w-fit px-2 py-1 border border-gray-600 rounded-2xl hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-500 rounded-2xl font-medium"
                >
                  {editingItem ? "Update Item" : "Create Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniERPInventory;